import { useState, useCallback } from 'react';
import type { AutomationSettings } from '../types';
import { DEFAULT_AUTOMATION_SETTINGS, DEFAULT_LOGO_URL } from '../utils/Constants';
import { buildNewsletterHtml } from '../utils/newsletterUtils';
import { apiClient } from '../services/apiClient';
import { toastService } from '../services/toastService';
import { useLanguage } from '../contexts/LanguageContext';
import { Logger } from '../utils/logger';

export function useSettings() {
  // V4-NEWSLETTER-TEMPLATES-FR-ONLY-DUPLICATED 2026-07-30 — `useLanguage` est remonté
  // au-dessus des `useState` : la graine du gabarit newsletter en dépend désormais.
  const { t, language } = useLanguage();
  const [automationSettings, setAutomationSettings] = useState<AutomationSettings>(DEFAULT_AUTOMATION_SETTINGS);
  const [systemPrompt, setSystemPromptState] = useState<string>("");
  const [isPositioningTemplate, setIsPositioningTemplate] = useState<boolean>(true);
  // V4-ONBOARDING-CHECKLIST-OPTIONAL — préférence de veille sectorielle du space
  // (undefined = non défini, false = personal branding sans veille → topics/sources
  // optionnels dans la checklist). Suit le même plumbing que isPositioningTemplate.
  const [sectorWatchEnabled, setSectorWatchEnabled] = useState<boolean | undefined>(undefined);
  const [logoUrl, setLogoUrl] = useState<string>(DEFAULT_LOGO_URL);
  // Graine de l'éditeur de gabarit : construite une fois au montage, dans la locale
  // active, depuis la source unique `buildNewsletterHtml` (initialiseur paresseux pour
  // ne pas régénérer le HTML à chaque rendu).
  const [newsletterTemplate, setNewsletterTemplate] = useState<string>(() =>
    buildNewsletterHtml([], t.newsletterTemplate, { lang: language }),
  );
  const [isLoadingPositioning, setIsLoadingPositioning] = useState(false);

  // Fetch automation settings from backend.
  // V4-E2E-2026-06-11-01 (2026-06-25) — la route backend GET /automation/{space_id}
  // est commentée (main.py, V4-DEAD-ROUTES) : l'appeler retournait un 404 silencieux
  // + une erreur console répétée à chaque chargement du dashboard. On NE l'appelle
  // plus pour l'instant ; on garde les valeurs par défaut. À réactiver (retirer ce
  // court-circuit) quand la feature Automation sera réimplémentée côté backend.
  const fetchAutomationSettings = useCallback(async (_spaceId: string) => {
    // No-op tant que la route backend n'est pas rétablie. setAutomationSettings reste
    // sur DEFAULT_AUTOMATION_SETTINGS (état initial).
    return;
  }, []);

  // Save automation settings to backend
  const updateAutomationSettings = useCallback(async (spaceId: string, settings: AutomationSettings) => {
    const prev = automationSettings;
    setAutomationSettings(settings);
    try {
      await apiClient.put(`/automation/${spaceId}`, {
        watch_config: {
          enabled: settings.watch.enabled,
          frequencyDays: settings.watch.frequencyDays,
          periodDays: settings.watch.periodDays,
          preferredHour: settings.watch.preferredHour,
        },
        generation_config: {
          enabled: settings.generation.enabled,
          channelIds: settings.generation.channelIds,
          frequencyDays: settings.generation.frequencyDays,
          postsPerIteration: settings.generation.postsPerIteration,
        },
        publication_config: {
          enabled: settings.publication.enabled,
          channelIds: settings.publication.channelIds,
          frequencyDays: settings.publication.frequencyDays,
          postsPerIteration: settings.publication.postsPerIteration,
        },
      });
    } catch (error) {
      setAutomationSettings(prev);
      Logger.error('Failed to save automation settings:', error);
      toastService.error(t.toasts?.saveError || 'Erreur lors de la sauvegarde');
    }
  }, [automationSettings, t]);

  // Fetch positioning from backend.
  // Fix V4-AGENCY-3 (2026-05-21) : on accepte un `spaceId` optionnel pour ne
  // plus laisser le backend deviner ("first owned space" → data leak entre
  // spaces d'une agence multi-tenant).
  const fetchPositioning = useCallback(async (spaceId?: string) => {
    setIsLoadingPositioning(true);
    try {
      const url = spaceId ? `/positioning/?space_id=${encodeURIComponent(spaceId)}` : '/positioning/';
      const response = await apiClient.get(url);
      if (response.data?.positioning_context) {
        setSystemPromptState(response.data.positioning_context);
      } else {
        // Si le space cible n'a pas de positionnement, on RESET côté state pour
        // éviter d'afficher celui d'un space précédemment chargé (bug agence).
        setSystemPromptState("");
      }
      if (response.data?.is_template !== undefined) {
        setIsPositioningTemplate(response.data.is_template);
      }
      // V4-ONBOARDING-CHECKLIST-OPTIONAL — null/absent ⇒ undefined (veille requise).
      setSectorWatchEnabled(
        response.data?.sector_watch_enabled === null || response.data?.sector_watch_enabled === undefined
          ? undefined
          : response.data.sector_watch_enabled,
      );
    } catch (error) {
      Logger.error('Failed to fetch positioning:', error);
    } finally {
      setIsLoadingPositioning(false);
    }
  }, []);

  // V4-POSITIONING-DOUBLE-PUT (2026-07-03, root cause réel) — ce setter faisait
  // AUSSI son propre PUT /positioning/ (sans `source`, découvert par capture réseau
  // en live). Tous ses appelants (PositioningTab.onUpdatePrompt, usePositioningOnboarding)
  // l'invoquent UNIQUEMENT après qu'un autre appel (updatePositioning/applyChanges/
  // restoreVersion) a déjà persisté le même contenu côté backend — ce PUT était donc
  // un doublon pur, et pouvait même s'exécuter sans espace explicite selon l'appelant.
  // Ce setter ne fait plus que synchroniser l'état local (consommé ailleurs dans l'app,
  // ex: GenerateModal) ; la persistance reste entièrement du ressort de l'appelant.
  const setSystemPrompt = useCallback((prompt: string) => {
    setSystemPromptState(prompt);
    setIsPositioningTemplate(false);
  }, []);

  return {
    automationSettings,
    setAutomationSettings,
    fetchAutomationSettings,
    updateAutomationSettings,
    systemPrompt,
    setSystemPrompt,
    isPositioningTemplate,
    sectorWatchEnabled,
    fetchPositioning,
    isLoadingPositioning,
    logoUrl,
    setLogoUrl,
    newsletterTemplate,
    setNewsletterTemplate
  };
}
